/**
 * Client-Side Error Handler
 *
 * Production-grade error handling with logging and monitoring integration.
 *
 * Architecture:
 * - Error Handler: Logs errors and sends to monitoring (Sentry, etc.)
 * - React Query: Shows toasts via global error handling
 * - Components: Override with custom toasts when needed
 * - Error Boundaries: Catch catastrophic failures
 *
 * ─── `showToUser` USED TO BE ACCEPTED AND THROWN AWAY ──────────────────────
 *
 * `ErrorHandlerOptions` has always declared `showToUser` and `userMessage`, and
 * `handleError` destructured only `rethrow` and `logLevel`. Both were silently
 * discarded. 38 call sites passed them; 26 happened to be rescued by React
 * Query's global mutation `onError`, and 12 were genuinely silent — the user
 * clicked, the operation failed, and nothing said so (EmployeeModal x5,
 * ReviewReplyModal x2, team-switcher x2, MenuGroupModal, OrderingLinksSection,
 * RestaurantModal).
 *
 * That is the failure the error-UX standard exists to prevent, and its lint
 * could not see it: `no-raw-toast-error.cjs` checks how a message is WORDED,
 * never that one EXISTS, because there is no toast call site to inspect.
 *
 * The handler still renders nothing itself. It delegates to a notifier
 * registered by the app shell — the same injection shape as
 * {@link setMonitoringCallback} — so this package stays UI-free and the
 * behaviour stays testable.
 */

import type { ErrorContext, ErrorHandlerOptions } from '@order-up/common';
import { AppError, translateError } from '@order-up/common';
import { ClientLogger } from './logger';

/**
 * Optional monitoring callback (set this to integrate Sentry, etc.)
 *
 * @example
 * ```typescript
 * import * as Sentry from '@sentry/nextjs';
 * setMonitoringCallback((error, context) => {
 *   Sentry.captureException(error, {
 *     contexts: { custom: context }
 *   });
 * });
 * ```
 */
let monitoringCallback: ((error: unknown, context: ErrorContext) => void) | null = null;

export function setMonitoringCallback(callback: (error: unknown, context: ErrorContext) => void) {
    monitoringCallback = callback;
}

/**
 * How a user-facing message actually reaches the screen.
 *
 * Registered once by the app shell (`AppProviders`), which owns the toast
 * library. Returning an id lets a later, MORE SPECIFIC message replace an
 * earlier generic one — see {@link notifyUser}.
 */
export type UserNotifier = (message: string, error: unknown) => string | number | void;

let userNotifier: UserNotifier | null = null;
let dismissNotice: ((id: string | number) => void) | null = null;
let warnedNoNotifier = false;

export function setUserNotifier(
    notify: UserNotifier,
    dismiss?: (id: string | number) => void,
) {
    userNotifier = notify;
    dismissNotice = dismiss ?? null;
}

/** Test seam: forget the registered notifier. */
export function __resetUserNotifier() {
    userNotifier = null;
    dismissNotice = null;
    warnedNoNotifier = false;
}

/**
 * Which errors have already been shown, and as which toast.
 *
 * 🔴 WHY THIS EXISTS. React Query's global mutation `onError` fires BEFORE the
 * component's `catch` resumes, so a mutation failure that a component also
 * reports would produce TWO toasts — a generic one then a specific one. A
 * WeakMap keyed on the error object lets the second call REPLACE the first
 * instead of stacking on it, so the rule is: never zero, never two, and the
 * more specific message wins.
 *
 * Non-object errors (a thrown string) cannot key a WeakMap; they fall through
 * and are shown, because a duplicate is a far smaller failure than silence.
 */
const shownFor = new WeakMap<object, string | number>();

/** Record that the global handler has already surfaced this error. */
export function markUserNotified(error: unknown, id: string | number | void) {
    if (error && typeof error === 'object' && id != null) {
        shownFor.set(error as object, id);
    }
}

function notifyUser(error: unknown, message: string) {
    if (!userNotifier) {
        // A missing wiring is the ORIGINAL bug in a new costume, so it is loud
        // rather than silent — once, so a broken app is not also a spammy one.
        if (!warnedNoNotifier) {
            warnedNoNotifier = true;
            console.warn(
                '[errorHandler] showToUser was requested but no notifier is registered. ' +
                    'Call setUserNotifier() in the app shell, or the message reaches nobody.',
            );
        }
        return;
    }

    const key = error && typeof error === 'object' ? (error as object) : null;
    const previous = key ? shownFor.get(key) : undefined;
    if (previous != null) {
        // Already surfaced. Replace it only if we can, so the specific message
        // wins without ever showing both.
        if (!dismissNotice) return;
        dismissNotice(previous);
    }

    const id = userNotifier(message, error);
    if (key && id != null) shownFor.set(key, id);
}

export class ClientErrorHandler {
    private logger: ClientLogger;

    constructor(logger?: ClientLogger) {
        this.logger = logger || new ClientLogger({ service: 'ErrorHandler' });
    }

    /**
     * Handle an error with automatic logging and monitoring
     *
     * NOTE: This does NOT show toasts. Toast display is handled by:
     * - React Query global error handling (for queries/mutations)
     * - Component-level error handling (for custom messages)
     */
    handleError(
        error: unknown,
        context: ErrorContext,
        options: ErrorHandlerOptions = {}
    ): void {
        const {
            rethrow = false,
            logLevel = 'error',
            showToUser = false,
            userMessage,
        } = options;

        // Build log context
        const logContext = {
            component: context.component,
            operation: context.operation,
            tenantId: context.tenantId?.toString(),
            restaurantId: context.restaurantId?.toString(),
            userId: context.userId,
        };

        const errorMessage = this.getErrorMessage(error);

        // Log the error with context
        const loggerWithContext = this.logger.child(logContext);
        const logMsg = `${context.operation || 'Operation'} failed: ${errorMessage}`;

        if (logLevel === 'error') {
            loggerWithContext.error(logMsg, error, context.metadata);
        } else {
            loggerWithContext.warn(logMsg, context.metadata);
        }

        // Send to monitoring service (Sentry, etc.) if configured
        if (monitoringCallback) {
            monitoringCallback(error, context);
        }

        // Tell the user, if the caller asked. `userMessage` beats the translated
        // one because a caller that bothered to write a specific sentence knows
        // more about the operation than the generic catalogue does.
        if (showToUser) {
            notifyUser(error, userMessage ?? getUserFriendlyMessage(error));
        }

        // Rethrow if requested (useful for error boundaries)
        if (rethrow) {
            throw error;
        }
    }

    /**
     * Get error message from unknown error type
     */
    getErrorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }
        if (typeof error === 'string') {
            return error;
        }
        return 'An unexpected error occurred';
    }

    /**
     * Check if error is operational (expected) vs programming error
     */
    isOperationalError(error: unknown): boolean {
        return error instanceof AppError;
    }
}

/**
 * Get user-friendly error message for display in UI
 *
 * Use this when you need to show a toast in your component.
 *
 * @example
 * ```typescript
 * try {
 *   await someOperation();
 * } catch (error) {
 *   errorHandler.handleError(error, {...}); // Logs only
 *   toast.error(getUserFriendlyMessage(error)); // Show toast
 * }
 * ```
 */
export function getUserFriendlyMessage(error: unknown): string {
    return translateError(error).message;
}

/**
 * Create a client error handler with optional logger
 */
export function createErrorHandler(logger?: ClientLogger): ClientErrorHandler {
    return new ClientErrorHandler(logger);
}

/**
 * Default error handler instance
 */
export const errorHandler = new ClientErrorHandler();

/**
 * Alias for createErrorHandler (backwards compatibility)
 */
export const createClientErrorHandler = createErrorHandler;
